import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalSubheading } from "./shared/ModalColumn";
import { formatCurrency, formatDateOnly, formatVolume } from "@/lib/format";
import { formatParcelWeight } from "./shared/parcelTotals";
import { useCostUnit } from "@/hooks/useCostUnit";
import { useVolumeUnit } from "@/hooks/useVolumeUnit";
import { priorityBadge } from "@/lib/priority";
import { cn } from "@/lib/cn";
import type { Order } from "@/types/orders";

/**
 * «09:00:00» → «09:00». The API returns `TimeField`s with seconds; a delivery
 * window never uses them, so they are noise in a read view. A value that is not
 * `HH:MM:SS` is passed through unchanged rather than being truncated blindly.
 */
function trimSeconds(value: string): string {
  return /^\d{2}:\d{2}:\d{2}$/.test(value) ? value.slice(0, 5) : value;
}

/** Tags shown before the «+N» overflow chip (§5.6). */
const TAG_LIMIT = 6;

interface Props {
  order: Order;
  /** Human-readable proof-of-delivery method, resolved by the host modal. */
  podMethod: string;
  /**
   * Let the driver-notes strip absorb the column's leftover height so it
   * finishes level with the neighbouring columns (mockup, 2026-08-25).
   *
   * Opt-in and driven by the host, because whether the notes strip is the LAST
   * block in the column depends on the tenant's `parcels_enabled`: with parcels
   * on, the parcels box sits below it and takes that role instead. Two blocks
   * both claiming `flex-1` would split the slack and neither would reach the
   * bottom edge.
   */
  growNotes?: boolean;
}

/**
 * «ДОСТАВКА / ГРУЗ / КЛИЕНТ» — the middle column, three label-value grids and
 * the driver-notes block.
 *
 * Every value here is read-only display. The labels are the same i18n keys the
 * pre-rebuild modal used, so behaviour won by earlier live reviews (and pinned
 * by `OrderDetailModal.test.tsx`) survives the recompose unchanged.
 */
export function OrderViewFacts({ order, podMethod, growNotes = false }: Props) {
  const { t, i18n } = useTranslation();
  const costUnit = useCostUnit();
  const volumeUnit = useVolumeUnit();
  const badge = priorityBadge(order.priority);

  const notes = (order.notes ?? "").trim();

  return (
    /*
      A flex column that stays shrinkABLE (`min-h-0`) in both states: with parcels
      ON this block plus the fixed-height parcels box can exceed the column's
      track, and a rigid block would push the column into a scroll — a second
      scroller, which the no-internal-scroll guarantee forbids. Being able to give
      space back is what keeps the parcels list the dialog's only scroller.

      What `growNotes` now also decides is whether this block CLAIMS the slack.
      `flex-1` sets `flex-basis: 0`, which sizes the block from the free space
      rather than from its content — so with parcels ON it was resolving to a
      height BELOW what its children need, and flexbox took the difference out of
      the one shrinkable child, the notes strip. Measured at 1280 on order 94: the
      column had 51px to spare (17 + 400 + 219 against 687), yet the block was
      pinned to 400px and the notes strip was squeezed from 67px to 33px, painting
      «Заметок для водителя нет» through the ПОСЫЛКИ header below it. Sizing from
      content (`flex-none` + `min-h-0`) lets the strip keep its intrinsic height
      while the parcels box below goes on absorbing the leftover, which is exactly
      the division of labour `growNotes` already describes.
    */
    <div
      className={cn(
        "flex min-h-0 flex-col gap-3",
        growNotes ? "flex-1" : "flex-none",
      )}
    >
      <dl className="grid grid-cols-3 gap-x-3 gap-y-3">
        {/*
          `formatDateOnly`, not the raw ISO: the mockup shows «25 мая 2026», and
          a dispatcher reading a date should not have to parse `YYYY-MM-DD`. The
          *Only* variant is required — `delivery_date` is a bare date, and the
          plain parse reads it as UTC midnight, rendering the PREVIOUS day in any
          negative-offset timezone (the BUG-355 / BUG-491 family).
        */}
        <Fact
          label={t("orders.detail.deliveryDate")}
          value={
            order.delivery_date ? formatDateOnly(order.delivery_date) : null
          }
        />
        <Fact
          label={t("orders.detail.timeWindow")}
          value={
            order.time_window_start && order.time_window_end
              ? // «09:00 — 18:00», not the API's «09:00:00». The seconds are
                // always zero on a planning window and carry no information.
                `${trimSeconds(order.time_window_start)} — ${trimSeconds(
                  order.time_window_end,
                )}`
              : null
          }
        />
        <Fact
          label={t("orders.detail.deliveryTypeShort")}
          value={
            order.delivery_type === "pickup"
              ? t("orders.delivery_type_pickup")
              : t("orders.delivery_type_delivery")
          }
        />
        <Fact
          label={t("orders.detail.strictTimeWindow")}
          value={order.strict_time_window ? t("common.yes") : t("common.no")}
        />
        <Fact label={t("orders.detail.podMethod")} value={podMethod} />
        <Fact
          label={t("orders.detail.serviceTimeShort")}
          value={
            order.service_time_min != null
              ? `${order.service_time_min} ${t("units.min")}`
              : null
          }
        />
      </dl>

      <ModalSubheading>{t("orders.detail.cargo")}</ModalSubheading>

      <dl className="grid grid-cols-3 gap-x-3 gap-y-3">
        <Fact
          label={t("orders.detail.weight")}
          // `formatParcelWeight` drops the DecimalField's trailing zeros, so a
          // weight of «1.000» reads as «1 кг» the way the mockup shows it —
          // rather than promising three decimals of precision nobody entered.
          value={
            order.weight_kg
              ? `${formatParcelWeight(Number(order.weight_kg))} ${t("units.kg")}`
              : null
          }
        />
        <Fact
          label={t("orders.detail.volumeShort")}
          value={
            order.volume_m3 ? formatVolume(order.volume_m3, volumeUnit) : null
          }
        />
        <Fact
          label={t("orders.detail.pallets")}
          value={
            order.pallets && Number(order.pallets) > 0 ? order.pallets : null
          }
        />
        <Fact
          label={t("orders.detail.itemCost")}
          value={
            order.item_cost
              ? formatCurrency(Number(order.item_cost), costUnit, i18n.language)
              : null
          }
        />
        <Fact
          label={t("orders.detail.priority")}
          value={
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold",
                badge.bg,
                badge.text,
              )}
            >
              <span
                className={cn("h-1.5 w-1.5 rounded-full", badge.dot)}
                aria-hidden="true"
              />
              {t(`orders.priorityLevel.${badge.level}`)}
            </span>
          }
        />
        <Fact
          label={t("orders.detail.tags")}
          value={<TagList tags={order.tags} />}
        />
      </dl>

      <ModalSubheading>{t("orders.detail.customer")}</ModalSubheading>

      {/*
        Three columns, matching ДОСТАВКА and ГРУЗ above (owner, 2026-08-25: «имя,
        телефон и емейл в одну строку делай» — and both mockups show the three on
        one row). This was `grid-cols-2` with Email forced to `col-span-2`, which
        is what dropped Email onto a second line.

        It fits because every `Fact` is `min-w-0` and these three are `truncate`d:
        a long address must never be abbreviated, but a name, a phone and an email
        are identifiers, so an ellipsis with the full value in `title` is the
        honest trade. Without `min-w-0` a long unbroken email would instead widen
        its track and scroll the dialog sideways.
      */}
      <dl className="grid grid-cols-3 gap-x-3 gap-y-3">
        {/* A name is an identifier, so an ellipsis is acceptable here where it
            is not for an address — the full value stays in `title`. */}
        <Fact
          label={t("orders.detail.name")}
          value={order.customer_name || null}
          truncate
        />
        <Fact
          label={t("orders.detail.phone")}
          value={order.customer_phone || null}
          truncate
        />
        <Fact
          label={t("orders.detail.email")}
          value={order.customer_email || null}
          truncate
        />
      </dl>

      {/*
        Driver notes — a full-width tinted strip (mockup). `bg-gray-50` is a
        structural neutral: this is a surface wash, not a state or a meaning.
      */}
      <div
        /*
          Addressed by test id, not by walking up from the label with
          `.closest("div")`: the label sits at a DIFFERENT depth in the two
          branches below (one line when a note exists, stacked when it does not),
          so the walk landed on this strip only while the fixture's note was
          empty and silently retargeted to an inner wrapper otherwise.
        */
        data-testid="driver-notes-strip"
        className={cn(
          "rounded-xl bg-gray-50 px-3 py-3",
          // Grows only when this strip is the column's LAST block (parcels off).
          // With parcels on, the parcels box below takes that role and this
          // strip keeps its intrinsic height — two blocks both claiming the
          // slack would split it and neither would reach the bottom edge.
          growNotes
            ? "flex min-h-0 flex-1 flex-col"
            : // Never grows, but must still be allowed to SHRINK, so the block
              // above can give height back to the parcels box instead of
              // overflowing the column.
              //
              // `overflow-hidden` is the SECOND half of the overlap fix, and the
              // one that makes it robust. The parent now sizes from its content
              // (see the note there), so this strip is no longer squeezed in the
              // normal case. But `shrink` still permits it — that is deliberate,
              // it is how the column gives space back instead of opening a second
              // scroller — and while the box was `overflow: visible`, any shrink
              // meant the text kept painting its full height OUTSIDE the box and
              // straight through whatever sat below. Measured at 1280 before the
              // fix: a 24px box against 55px of content, «Заметок для водителя
              // нет» painting to y=577 across a ПОСЫЛКИ header band at y=553–569.
              // Clipping keeps a shrink ugly-but-contained rather than overlapping.
              //
              // Clipping, not `flex-1`: with parcels ON the parcels box is the
              // block that claims the column's slack, and a second `flex-1` here
              // would split it so neither reached the bottom edge (the failure
              // `OrderDetailViewLayout` pins with `not.toContain("flex-1")`).
              "min-h-0 shrink overflow-hidden",
        )}
      >
        {/*
          Label and text share ONE line when a note exists (mockup, 2026-08-25) —
          a short note is the common case and a stacked label wasted a whole row
          of the height budget on it. With no note the strip stacks instead, so
          the placeholder is not squeezed against the label.

          `items-baseline` rather than `items-center`: the label is 11px and the
          note 14px, and aligning their baselines is what makes them read as one
          line rather than two things that happen to be side by side.
        */}
        {notes ? (
          <div className="flex min-h-0 flex-1 items-baseline gap-3">
            <p className="shrink-0 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              {t("orders.detail.driverNotes")}
            </p>
            <div className="min-w-0 flex-1">
              <ExpandableNotes notes={notes} />
            </div>
          </div>
        ) : (
          <>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              {t("orders.detail.driverNotes")}
            </p>
            <p className="mt-1.5 text-sm italic text-gray-400">
              {t("orders.detail.noDriverNotes")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

interface FactProps {
  label: string;
  value: React.ReactNode;
  /** One-line with a `title` — for identifier-ish values (name, phone, email). */
  truncate?: boolean;
  className?: string;
}

/** One label-value pair. A missing value renders «—», never an empty gap. */
function Fact({ label, value, truncate = false, className }: FactProps) {
  const isEmpty = value == null || value === "";
  return (
    <div className={cn("min-w-0", className)}>
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd
        className={cn(
          "mt-0.5 text-sm font-medium text-gray-900",
          truncate && "truncate",
        )}
        title={truncate && typeof value === "string" ? value : undefined}
      >
        {isEmpty ? "—" : value}
      </dd>
    </div>
  );
}

/** Tags wrap and overflow into a «+N» chip — never a sideways-scrolling strip. */
function TagList({ tags }: { tags: string[] }) {
  if (!tags || tags.length === 0) return null;
  const visible = tags.slice(0, TAG_LIMIT);
  const overflow = tags.length - visible.length;
  return (
    <span className="flex flex-wrap gap-1" title={tags.join(", ")}>
      {visible.map((tag) => (
        <span
          key={tag}
          className="max-w-[120px] truncate rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
        >
          {tag}
        </span>
      ))}
      {overflow > 0 && (
        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
          +{overflow}
        </span>
      )}
    </span>
  );
}

/** Driver notes clamp to four lines with an in-place «Показать полностью». */
function ExpandableNotes({ notes }: { notes: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <p
        className={cn(
          "whitespace-pre-line break-words text-sm text-gray-700",
          !expanded && "line-clamp-4",
        )}
      >
        {notes}
      </p>
      {!expanded && notes.length > 160 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 text-xs font-medium text-brand-primary hover:underline"
        >
          {t("orders.detail.showFullNotes")}
        </button>
      )}
    </>
  );
}
